<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import AiAgentsAPI from '../../../../../api/aiAgents';

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const relations = ref([]);
const isLoading = ref(false);
const selectedInboxId = ref('');

const allInboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

const assignedInboxIds = computed(() =>
  relations.value.map(relation => relation.inbox?.id)
);

const availableInboxes = computed(() =>
  allInboxes.value.filter(inbox => !assignedInboxIds.value.includes(inbox.id))
);

const loadRelations = async () => {
  isLoading.value = true;
  try {
    const { data } = await AiAgentsAPI.getInboxes(props.agent.id);
    relations.value = data;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
  loadRelations();
});

const addInbox = async () => {
  if (!selectedInboxId.value) return;
  await AiAgentsAPI.addInbox(props.agent.id, {
    inbox_id: Number(selectedInboxId.value),
    status: 0,
    priority: 0,
  });
  selectedInboxId.value = '';
  await loadRelations();
};

const removeInbox = async relationId => {
  await AiAgentsAPI.removeInbox(props.agent.id, relationId);
  await loadRelations();
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-if="isLoading" class="text-sm text-n-slate-10">
      {{ t('AI_AGENTS.LOADING') }}
    </div>

    <div v-else class="grid gap-4">
      <div class="flex gap-2">
        <select
          v-model="selectedInboxId"
          class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
        >
          <option value="">{{ t('AI_AGENTS.INBOXES.SELECT') }}</option>
          <option
            v-for="inbox in availableInboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90"
          :disabled="!selectedInboxId"
          @click="addInbox"
        >
          {{ t('AI_AGENTS.INBOXES.ADD') }}
        </button>
      </div>

      <table
        v-if="relations.length"
        class="w-full text-sm text-left border-collapse"
      >
        <thead>
          <tr class="border-b border-n-slate-6 text-n-slate-10">
            <th class="py-2">{{ t('AI_AGENTS.INBOXES.NAME') }}</th>
            <th class="py-2">{{ t('AI_AGENTS.INBOXES.STATUS') }}</th>
            <th class="py-2">{{ t('AI_AGENTS.INBOXES.PRIORITY') }}</th>
            <th class="py-2">{{ t('AI_AGENTS.TABLE.ACTIONS') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="relation in relations"
            :key="relation.id"
            class="border-b border-n-slate-6"
          >
            <td class="py-3">{{ relation.inbox?.name }}</td>
            <td class="py-3">{{ relation.status }}</td>
            <td class="py-3">{{ relation.priority }}</td>
            <td class="py-3">
              <button
                class="text-n-ruby-9 hover:text-n-ruby-10"
                @click="removeInbox(relation.id)"
              >
                {{ t('AI_AGENTS.INBOXES.REMOVE') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="text-sm text-n-slate-10">
        {{ t('AI_AGENTS.INBOXES.NO_RESULTS') }}
      </div>
    </div>
  </div>
</template>
